import React, { useEffect, useState } from 'react'
import { Table, Pagination, Space, Spin, Button, message, Tooltip } from 'antd'
import { apiRequest } from '../helpers'
import { useLanguage } from './context/LanguageContext'
import { ReloadOutlined } from '@ant-design/icons'

const KnowledgeBaseItemList = ({ knowledgebaseId }) => {
  const [loading, setLoading] = useState(false)
  const [resetLoading, setResetLoading] = useState(false)
  const [data, setData] = useState([])
  const [columns, setColumns] = useState([])
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const languageContext = useLanguage()

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 1,
  })

  const [filter, setFilter] = useState({
    search: '',
    sortColumn: '',
    sortDirection: '',
    filters: [],
  })

  useEffect(() => {
    if (knowledgebaseId) {
      getItems()
    } else {
      setData([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knowledgebaseId, pagination.current, pagination.pageSize, filter])

  const getItems = async () => {
    if (!knowledgebaseId) return

    setLoading(true)

    try {
      const url = getPaginationUrl()
      const result = await apiRequest(url, 'GET')

      if (!result?.ok || !result?.data?.data) {
        setData([])
        setLoading(false)
        return
      }

      if (result.data.hasOwnProperty('columns')) {
        setColumns(result.data.columns)
      }

      setPagination(prevPagination => ({
        ...prevPagination,
        current: result.data.meta.currentPage,
        pageSize: result.data.meta.itemsPerPage,
        total: result.data.meta.totalItems,
      }))

      // Transform data and set state
      const itemsData = result.data.data.map(item => ({
        ...item,
        key: item.id,
      }))

      setData(itemsData)
    } catch (error) {
      console.error('Error fetching knowledgebase items:', error)
    } finally {
      setLoading(false)
    }
  }

  const getPaginationUrl = () => {
    let url = `${process.env.REACT_APP_API}process/kb/${knowledgebaseId}/items`
    if (pagination.pageSize) url += `?limit=${pagination?.pageSize}`
    if (pagination.current) url += `&page=${pagination.current}`
    if (filter.search) url += `&search=${filter.search}`

    if (filter.sortColumn && filter.sortDirection) {
      let key = filter.sortColumn
      url += `&sortBy=${key}:${filter.sortDirection}`
    }

    if (Object.keys(filter.filters).length) {
      Object.keys(filter.filters).forEach(key => {
        let ids = filter.filters[key]
        if (!Array.isArray(ids)) return
        url += `&filter.${key}=$in:${ids.join(',')}`
      })
    }

    return url
  }

  const resetSelectedItems = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning(languageContext.t('PleaseSelectItemsToReset'))
      return
    }

    setResetLoading(true)

    try {
      const url = `${process.env.REACT_APP_API}process/kb/items/reset`
      const result = await apiRequest(url, 'POST', { itemIds: selectedRowKeys })

      if (!result?.ok) {
        message.error(result?.data?.message || languageContext.t('ErrorResettingItems'))
        return
      }

      message.success(result?.data?.data?.message || languageContext.t('ItemsResetSuccessfully'))
      setSelectedRowKeys([])
      getItems() // Refresh the list
    } catch (error) {
      console.error('Error resetting knowledge base items:', error)
      message.error(languageContext.t('ErrorResettingItems'))
    } finally {
      setResetLoading(false)
    }
  }

  const onChange = (pagination, filters, sorter) => {
    let direction = ''
    if (sorter.order === 'ascend') direction = 'ASC'
    if (sorter.order === 'descend') direction = 'DESC'

    setFilter({
      ...filter,
      sortDirection: direction,
      sortColumn: sorter.field,
      filters: filters,
    })
  }

  const getTableColumns = () => {
    if (!columns?.length) return []

    return columns.map(col => ({
      title: col.label,
      dataIndex: col.key,
      key: col.key,
      sorter: true,
      render: text => {
        if (typeof text === 'boolean') {
          return text ? '✅' : '❌'
        }

        // Format date if the column is a date type
        if (col.type === 'date' && text) {
          return new Date(text).toLocaleString()
        }

        return String(text || '')
      },
    }))
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: selectedKeys => {
      setSelectedRowKeys(selectedKeys)
    },
  }

  if (!knowledgebaseId) {
    return null
  }

  return (
    <div className="knowledge-base-items-container" style={{ marginTop: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h4>{languageContext.t('KnowledgeBaseItems')}</h4>

        <Tooltip title={languageContext.t('ResetItemsTooltip')}>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            loading={resetLoading}
            disabled={selectedRowKeys.length === 0}
            onClick={resetSelectedItems}
          >
            {languageContext.t('ResetToPending')}
          </Button>
        </Tooltip>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Table
            rowSelection={rowSelection}
            columns={getTableColumns()}
            dataSource={data}
            pagination={false}
            rowKey="key"
            onChange={onChange}
            size="small"
            bordered
            className="dyn-table knowledge-base-items-table"
          />

          <Space style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end' }}>
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              showSizeChanger
              onChange={(page, pageSize) => {
                setPagination(prevPagination => ({
                  ...prevPagination,
                  current: page,
                  pageSize: pageSize,
                }))
              }}
            />
          </Space>
        </>
      )}
    </div>
  )
}

export default KnowledgeBaseItemList
